/**
 * SystemOpsApi — system status, scheduled tasks, task queue, circuit
 * breaker, admin operations (manual ingest, reprocess), and utility methods.
 *
 * Extracted from client.ts (CUR-547 god-object split, batch 2). Methods-only
 * by design: prototype methods are merged onto AdminAPIClient via applyMixins
 * in client.ts, so the flat `apiClient.method()` surface is unchanged.
 * Instance state lives in BaseApiClient — do not add fields here.
 */

import type { IngestionResult, KgQualityIndexResponse, ManualIngestRequest, SystemStatusResponse, TaskStatusResponse } from '../../../../shared/types/api';
import { APIError as SharedAPIError, buildApiUrl, createAuthHeaders, processApiResponse } from '../../../../shared/utils/api-helpers';
import type { ScheduledTasksResponse, TaskHistoryResponse } from '@/types/scheduled-tasks';
import { API_ENDPOINTS } from '@/utils/env';

import { BaseApiClient } from './base';

export class SystemOpsApi extends BaseApiClient {
  // ============================================================================
  // System Status Methods
  // ============================================================================

  async getSystemStatus(): Promise<SystemStatusResponse> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.adminStatus);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<SystemStatusResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  async getKgQualityIndex(): Promise<KgQualityIndexResponse> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.statsKgQualityIndex);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<KgQualityIndexResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  // Scheduled Tasks Methods
  // ============================================================================

  /**
   * Get status of all monitored scheduled tasks.
   * Returns summary counts and last execution details for each task.
   */
  async getScheduledTasks(): Promise<ScheduledTasksResponse> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.adminScheduledTasks);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<ScheduledTasksResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  /**
   * Get execution history for a specific scheduled task.
   * @param taskName - Beat schedule key (e.g., 'generate-daily-newsletter')
   * @param limit - Maximum number of history records to return (default 10)
   */
  async getScheduledTaskHistory(taskName: string, limit: number = 10): Promise<TaskHistoryResponse> {
    const url = buildApiUrl(this.baseUrl, `${API_ENDPOINTS.adminScheduledTaskHistory(taskName)}?limit=${limit}`);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<TaskHistoryResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }


  // Task Queue Methods
  async getTaskQueueStatus(options: { fresh?: boolean } = {}): Promise<any> {
    // `fresh` bypasses the server's 10s status cache (CUR-1506) — use after an action only.
    const base = buildApiUrl(this.baseUrl, API_ENDPOINTS.adminTaskQueue);
    const url = options.fresh ? `${base}?fresh=true` : base;
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<any>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    const payload = result.data!;
    return payload.data ?? payload;
  }

  /**
   * A 60-second ticket for opening the task-queue stream (CUR-2091). EventSource
   * cannot send headers, and the Auth0 token it used to carry in the URL was
   * written to the API's access log.
   */
  async getTaskQueueStreamTicket(): Promise<string> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.adminTaskQueueStreamTicket);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { method: 'POST', headers });
    const result = await processApiResponse<{ ticket: string }>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!.ticket;
  }

  /**
   * Vertex Batch lane snapshot for the Task Queue modal (CUR-1094).
   * Global infra data — buffer depth, in-flight jobs, awaiting-drip, today's throughput.
   */
  async getBatchLaneStatus(): Promise<import('@/types/batch-lane').BatchLaneData> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.adminBatchLane);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<any>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    const payload = result.data!;
    if (payload && payload.success === false) {
      throw new SharedAPIError(500, payload.error ?? 'Batch lane status unavailable');
    }
    return payload.data ?? payload;
  }

  async priorityProcessContent(payload: { content_id: string; force_reprocess?: boolean }): Promise<any> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.adminTaskQueuePriority);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ ...payload, force_reprocess: payload.force_reprocess ?? true }),
    });

    const result = await processApiResponse<any>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  // Circuit Breaker Methods
  async getCircuitBreakerStatus(): Promise<{
    is_open: boolean;
    consecutive_429s: number;
    total_429s_today: number;
    paused_until: string | null;
    threshold: number;
  }> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.circuitBreakerStatus);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<any>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    const payload = result.data!;
    return payload.data ?? payload;
  }

  async resetCircuitBreaker(): Promise<any> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.circuitBreakerReset);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      method: 'POST',
      headers,
    });

    const result = await processApiResponse<any>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  async getHealthCheck(): Promise<{ status: string; timestamp: string }> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.health);

    const response = await fetch(url);
    const result = await processApiResponse<{ status: string; timestamp: string }>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  // ============================================================================
  // Admin Operations
  // ============================================================================

  async triggerManualIngest(params: ManualIngestRequest = {}): Promise<IngestionResult> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.adminIngest);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(params),
    });

    const result = await processApiResponse<IngestionResult>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  async triggerAbsenceScan(): Promise<{ success: boolean; task_id: string; message: string }> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.adminTriggerAbsenceScan);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      method: 'POST',
      headers,
    });

    const result = await processApiResponse<{ success: boolean; task_id: string; message: string }>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  async getTaskStatus(taskId: string): Promise<TaskStatusResponse> {
    const url = buildApiUrl(this.baseUrl, `${API_ENDPOINTS.adminTasks}/${taskId}`);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<TaskStatusResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  async reprocessContent(slug: string, force: boolean = false, reason?: string): Promise<any> {
    const params: Record<string, any> = { force };
    if (reason) params.reason = reason;

    const url = buildApiUrl(this.baseUrl, `/admin/content/${slug}/reprocess`, params);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      method: 'POST',
      headers,
    });

    const result = await processApiResponse<any>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  async cancelStuckVideo(videoId: number): Promise<any> {
    const url = buildApiUrl(this.baseUrl, `/admin/youtube/cancel-stuck-video/${videoId}`);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      method: 'POST',
      headers,
    });

    const result = await processApiResponse<any>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  async cancelContent(contentId: string): Promise<any> {
    const url = buildApiUrl(this.baseUrl, `/admin/content/by-id/${contentId}/cancel`);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      method: 'POST',
      headers,
    });

    const result = await processApiResponse<any>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  async listFailedContent(contentType?: string): Promise<any> {
    const params: Record<string, any> = {};
    if (contentType) params.content_type = contentType;
    const url = buildApiUrl(this.baseUrl, '/admin/content/failed-videos', params);
    const headers = createAuthHeaders(await this.resolveToken());
    const response = await fetch(url, { headers });
    const result = await processApiResponse<any>(response);
    if (result.error) throw new SharedAPIError(result.status, result.error.message, result.error.code);
    return result.data!;
  }

  async getFailedContentSummary(): Promise<any> {
    const url = buildApiUrl(this.baseUrl, '/admin/content/failed-summary');
    const headers = createAuthHeaders(await this.resolveToken());
    const response = await fetch(url, { headers });
    const result = await processApiResponse<any>(response);
    if (result.error) throw new SharedAPIError(result.status, result.error.message, result.error.code);
    return result.data!;
  }

  async requeueFailedContent(body: {
    content_ids?: string[];
    reset_errors?: boolean;
    limit?: number;
    content_type?: string;
  }): Promise<any> {
    const url = buildApiUrl(this.baseUrl, '/admin/content/requeue-failed-videos');
    const headers = {
      ...createAuthHeaders(await this.resolveToken()),
      'Content-Type': 'application/json',
    };
    const response = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
    const result = await processApiResponse<any>(response);
    if (result.error) throw new SharedAPIError(result.status, result.error.message, result.error.code);
    return result.data!;
  }

  async reprocessContentById(contentId: string, force: boolean = false, reason?: string): Promise<any> {
    const params: Record<string, any> = { force };
    if (reason) params.reason = reason;
    const url = buildApiUrl(this.baseUrl, `/admin/content/by-id/${contentId}/reprocess`, params);
    const headers = createAuthHeaders(await this.resolveToken());
    const response = await fetch(url, { method: 'POST', headers });
    const result = await processApiResponse<any>(response);
    if (result.error) throw new SharedAPIError(result.status, result.error.message, result.error.code);
    return result.data!;
  }

  // ============================================================================
  // Utility Methods
  // ============================================================================

  async testConnection(): Promise<boolean> {
    try {
      await this.getHealthCheck();
      return true;
    } catch {
      return false;
    }
  }

  // Get current authentication status
  async isAuthenticated(): Promise<boolean> {
    const token = await this.resolveToken();
    return !!token && token.length > 0;
  }

}
